import {
  GET_PROFILE,
  LOGIN,
  LOGOUT,
  SET_TOKEN,
  UPDATE_PROFILE,
} from "./ActionTypes";
import axios from "axios";
import keycloak, { clearStoredKeycloakTokens } from "../keycloak";
import { getToken } from "../Shared/baseURL";

export const KEYCLOAK_AUTH_FLAG = "kcAuthenticated";
export const LOGOUT_IN_PROGRESS_KEY = "logoutInProgressAt";
export const LOGOUT_IN_PROGRESS_TTL_MS = 15000;
let inFlightProfileRequest = null;

const decodeJwtPayload = (token) => {
  if (!token || typeof token !== "string") return null;
  const parts = token.split(".");
  if (parts.length < 2) return null;
  try {
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(
      base64.length + ((4 - (base64.length % 4)) % 4),
      "=",
    );
    const json = atob(padded);
    return JSON.parse(json);
  } catch {
    return null;
  }
};

const pickRoleFromRoles = (roles = []) => {
  if (!Array.isArray(roles)) return null;
  const ignore = new Set([
    "offline_access",
    "uma_authorization",
    "default-roles-dev",
  ]);
  const candidate = roles.find((r) => r && !ignore.has(r));
  return candidate || null;
};

const getAccountTypeFromToken = () => {
  const parsed =
    keycloak?.tokenParsed || decodeJwtPayload(keycloak?.token) || {};
  const direct =
    parsed.profile_role ||
    parsed.accountType ||
    parsed.account_type ||
    parsed.role ||
    parsed.userType ||
    parsed.user_type ||
    null;
  if (direct) return direct;

  const realmRole = pickRoleFromRoles(parsed?.realm_access?.roles || []);
  if (realmRole) return realmRole;

  const resourceRoles = Object.values(parsed?.resource_access || {}).flatMap(
    (r) => r?.roles || [],
  );
  return pickRoleFromRoles(resourceRoles) || null;
};

const parseNumericSubjectId = (value) => {
  if (Number.isFinite(Number(value))) return Number(value);
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (/^\d+$/.test(trimmed)) return Number(trimmed);
  const match = trimmed.match(/\/(\d+)$/);
  return match && Number.isFinite(Number(match[1])) ? Number(match[1]) : null;
};

const extractSubjectId = (candidate) => {
  if (Number.isFinite(Number(candidate))) {
    return Number(candidate);
  }

  if (typeof candidate === "string") {
    return parseNumericSubjectId(candidate);
  }

  if (candidate && typeof candidate === "object") {
    const next =
      candidate.subjectId ?? candidate.id ?? candidate.value ?? candidate.href;
    if (Number.isFinite(Number(next))) {
      return Number(next);
    }
    if (typeof next === "string") {
      return parseNumericSubjectId(next);
    }
  }

  return null;
};

const buildSelectedSubjectSnapshot = (item) => {
  if (!item) return null;

  const name = String(
    item?.name || item?.title || item?.subjectName || item?.label || item || "",
  ).trim();

  if (!name) return null;

  const subjectId = extractSubjectId(item);
  const rawId =
    item?.id ?? item?.uuid ?? item?.href ?? subjectId ?? name.toLowerCase();

  return {
    id: String(rawId),
    subjectId: Number.isFinite(subjectId) ? subjectId : null,
    name,
  };
};

const normalizeSubjectIdOrder = (subjectIds = []) =>
  subjectIds
    .map((subjectId) => extractSubjectId(subjectId))
    .filter((subjectId) => Number.isFinite(subjectId))
    .map((subjectId) => Number(subjectId));

const mergeSelectedSubjectSnapshotsWithIds = (
  selectedSubjectSnapshots = [],
  selectedSubjectIds = [],
) => {
  const normalizedIds = normalizeSubjectIdOrder(selectedSubjectIds);
  if (!selectedSubjectSnapshots.length || !normalizedIds.length) {
    return selectedSubjectSnapshots;
  }

  const usedIds = new Set(
    selectedSubjectSnapshots
      .map((subject) => extractSubjectId(subject))
      .filter((subjectId) => Number.isFinite(subjectId))
      .map((subjectId) => Number(subjectId)),
  );
  let nextIdIndex = 0;

  return selectedSubjectSnapshots.map((subject) => {
    const existingSubjectId = extractSubjectId(subject);
    if (Number.isFinite(existingSubjectId)) {
      return subject;
    }

    while (
      nextIdIndex < normalizedIds.length &&
      usedIds.has(normalizedIds[nextIdIndex])
    ) {
      nextIdIndex += 1;
    }

    const subjectId = normalizedIds[nextIdIndex];
    if (!Number.isFinite(subjectId)) {
      return subject;
    }

    usedIds.add(subjectId);
    nextIdIndex += 1;

    return {
      ...subject,
      id: String(subjectId),
      subjectId,
    };
  });
};

const hasSameSubjectIdOrder = (left = [], right = []) => {
  const normalizedLeft = normalizeSubjectIdOrder(left);
  const normalizedRight = normalizeSubjectIdOrder(right);

  return (
    normalizedLeft.length === normalizedRight.length &&
    normalizedLeft.every(
      (subjectId, index) => subjectId === normalizedRight[index],
    )
  );
};

// Ensure missing profile fields are consistently null (not undefined)
// function normalizeProfile(src = {}) {
//   const p = { ...(src || {}) };

//   if (p.avatarURL == null) {
//     const fallbackAvatar = p.avatarUrl || p.avatar || p.picture || null;
//     if (fallbackAvatar != null) {
//       p.avatarURL = fallbackAvatar;
//     }
//   }

//   const ensure = (obj, key) => {
//     if (obj[key] === undefined) obj[key] = null;
//   };

//   [
//     "id",
//     "email",
//     "username",
//     "firstName",
//     "lastName",
//     "avatarURL",
//     "role",
//     "accountType",
//     "birthday",
//     "pronouns",
//     "countryOfBirth",
//     "howDidYouFindUs",
//     "bio",
//     "phoneNumber",
//     "disabilityStatus",
//     "ethnicity",
//   ].forEach((k) => ensure(p, k));

//   const org = p.organization || {};
//   ensure(org, "name");
//   ensure(org, "contactNumber");
//   p.organization = org;

//   return p;
// }

function normalizeProfile(src = {}) {
  if (!src) return null;
  const p = { ...src };
  const tokenParsed =
    keycloak?.tokenParsed || decodeJwtPayload(keycloak?.token) || {};
  const roleDetails =
    p?.roleDetails && typeof p.roleDetails === "object" ? p.roleDetails : {};
  const roleInstitution =
    roleDetails?.institution && typeof roleDetails.institution === "object"
      ? roleDetails.institution
      : null;
  const roleOrganization =
    roleDetails?.organization && typeof roleDetails.organization === "object"
      ? roleDetails.organization
      : null;
  const expertiseJobTitle = Array.isArray(p?.roleDetails?.expertise)
    ? p.roleDetails.expertise
        .map((entry) => String(entry?.jobTitle || "").trim())
        .find(Boolean) || null
    : null;
  const expertiseYearsOfExperience = Array.isArray(p?.roleDetails?.expertise)
    ? (p.roleDetails.expertise
        .map((entry) => entry?.yearsOfExperience)
        .find((value) => Number.isFinite(Number(value))) ?? null)
    : null;
  const uuid =
    p.uuid || p.id || p.userId || p.userUuid || (p.id ? String(p.id) : null);
  p.uuid = uuid ? String(uuid) : null;

  // Key network arrays
  p.following = Array.isArray(p.following) ? p.following : [];
  p.followers = Array.isArray(p.followers) ? p.followers : [];
  p.connections = Array.isArray(p.connections) ? p.connections : [];
  p.sentConnections = Array.isArray(p.sentConnections) ? p.sentConnections : [];
  p.receivedConnections = Array.isArray(p.receivedConnections)
    ? p.receivedConnections
    : [];
  p.pendingConnections = Array.isArray(p.pendingConnections)
    ? p.pendingConnections
    : [];

  if (p.avatarURL == null) {
    const fallbackAvatar = p.avatarUrl || p.avatar || p.picture || null;
    if (fallbackAvatar != null) p.avatarURL = fallbackAvatar;
  }

  if (p.username == null || String(p.username).trim() === "") {
    const fallbackUsername =
      tokenParsed?.preferred_username || tokenParsed?.username || null;
    if (fallbackUsername != null && String(fallbackUsername).trim() !== "") {
      p.username = String(fallbackUsername).trim();
    }
  }

  if (p.jobTitle == null || String(p.jobTitle).trim() === "") {
    p.jobTitle = expertiseJobTitle;
  }

  if (
    p.professionalExpertise == null ||
    String(p.professionalExpertise).trim() === ""
  ) {
    p.professionalExpertise = expertiseJobTitle;
  }

  if (
    p.yearsOfExperience == null ||
    String(p.yearsOfExperience).trim() === ""
  ) {
    p.yearsOfExperience = expertiseYearsOfExperience;
  }

  if (p.detailedRole == null || String(p.detailedRole).trim() === "") {
    p.detailedRole =
      roleDetails?.coordinatorRole || roleDetails?.mentorRole || null;
  }

  if (p.studiesAt == null || String(p.studiesAt).trim() === "") {
    p.studiesAt =
      roleInstitution?.name || p.placeOfStudy || p.placeOfTeaching || null;
  }

  if (p.institutionType == null || String(p.institutionType).trim() === "") {
    p.institutionType = roleInstitution?.institutionType || null;
  }

  if (p.institutionName == null || String(p.institutionName).trim() === "") {
    p.institutionName = roleInstitution?.name || null;
  }

  if (
    p.institutionContactNumber == null ||
    String(p.institutionContactNumber).trim() === ""
  ) {
    p.institutionContactNumber = roleInstitution?.contactNumber || null;
  }

  const nextOrganization =
    p.organization && typeof p.organization === "object"
      ? { ...p.organization }
      : roleOrganization
        ? { ...roleOrganization }
        : {};

  const ensure = (obj, key) => {
    if (obj[key] === undefined) obj[key] = null;
  };

  [
    "id",
    "email",
    "username",
    "firstName",
    "lastName",
    "avatarURL",
    "role",
    "accountType",
    "birthday",
    "pronouns",
    "countryOfBirth",
    "howDidYouFindUs",
    "bio",
    // Phone numbers are out of scope for this launch.
    // "phoneNumber",
    "disabilityStatus",
    "ethnicity",
    "yearsOfExperience",
    "jobTitle",
    "professionalExpertise",
    "detailedRole",
    "studiesAt",
    "institutionType",
    "institutionName",
    "institutionContactNumber",
    "newsletterSubscribed",
  ].forEach((k) => ensure(p, k));

  const org = nextOrganization;
  ensure(org, "name");
  ensure(org, "contactNumber");
  p.organization = org;

  return p;
}

export const userLogin = (data) => async (dispatch) => {
  dispatch({
    type: LOGIN,
    payload: data,
  });
};

export const getProfile = () => async (dispatch, getState) => {
  if (inFlightProfileRequest) {
    return inFlightProfileRequest;
  }

  inFlightProfileRequest = (async () => {
    let authToken = null;
    try {
      authToken = await getToken();
      if (!authToken) {
        return false;
      }
      const res = await axios.get("/sns/v1/users/me", {
        headers: { Authorization: authToken },
      });
      if (res && res.status === 200) {
        const normalized = normalizeProfile(res.data);
        const tokenAccountType = getAccountTypeFromToken();
        if (normalized.accountType == null && tokenAccountType) {
          normalized.accountType = tokenAccountType;
        }
        if (normalized.role == null && tokenAccountType) {
          normalized.role = tokenAccountType;
        }
        normalized.token = authToken;
        dispatch({ type: LOGIN, payload: normalized });
        dispatch({ type: GET_PROFILE, payload: normalized });
        dispatch({ type: SET_TOKEN, payload: authToken });
        return normalized;
      }
      return false;
    } catch (error) {
      const status = error?.response?.status;
      const statusText = error?.response?.statusText;
      const url = error?.config?.url;
      const method = error?.config?.method;
      const headers = error?.response?.headers;
      const data = error?.response?.data;
      try {
        console.error("[getProfile] Request failed", {
          status,
          statusText,
          url,
          method,
          headers,
          data,
          tokenRole:
            keycloak?.tokenParsed?.role ||
            keycloak?.tokenParsed?.accountType ||
            null,
          tokenSub: keycloak?.tokenParsed?.sub || null,
          tokenEmail: keycloak?.tokenParsed?.email || null,
        });
      } catch {
        console.error("[getProfile] Request failed", status, statusText);
      }

      // If backend doesn't have a user record yet, do not auto-create silently.
      // Registration should happen explicitly through the registration flow.
      try {
        const bootstrapTried =
          localStorage.getItem("me-bootstrap-attempted") === "1";
        const msg =
          (data &&
            (data.message || data.error || data.error_description || "")) ||
          "";
        const looksMissing =
          status === 404 ||
          status === 500 ||
          /not\s*found|no\s*user|missing/i.test(String(msg));
        if (!bootstrapTried && looksMissing) {
          localStorage.setItem("me-bootstrap-attempted", "1");
        }
      } catch {}

      // Keep the last fully hydrated profile in Redux during brief /me delays on
      // refresh instead of replacing it with a thin token fallback.
      try {
        const persistedProfile = normalizeProfile(
          getState?.()?.user?.profile || getState?.()?.user?.user || null,
        );
        const hasHydratedProfile = Boolean(
          persistedProfile &&
          persistedProfile.profileSource !== "token-fallback" &&
          (persistedProfile.id ||
            persistedProfile.uuid ||
            persistedProfile.email ||
            persistedProfile.username),
        );

        if (hasHydratedProfile) {
          if (authToken) {
            dispatch({ type: SET_TOKEN, payload: authToken });
          }
          return false;
        }
      } catch {}

      // Fallback: build minimal profile from Keycloak token so UI can render
      try {
        const tp = keycloak?.tokenParsed || {};
        const fallbackRaw = {
          id: tp.sub || null,
          email: tp.email || null,
          username: tp.preferred_username || tp.email || null,
          firstName: tp.given_name || null,
          lastName: tp.family_name || null,
          avatarURL: tp.picture || null,
          role: tp.role || tp.accountType || tp.preferred_role || null,
          profileSource: "token-fallback",
        };
        const fallback = normalizeProfile(fallbackRaw);
        if (fallback.role) fallback.role = String(fallback.role).toLowerCase();
        if (fallback.username || fallback.email) {
          dispatch({ type: LOGIN, payload: fallback });
          dispatch({ type: GET_PROFILE, payload: fallback });
          return fallback;
        }
      } catch {}
      return false;
    }
  })();

  try {
    return await inFlightProfileRequest;
  } finally {
    inFlightProfileRequest = null;
  }
};

export const updateReduxProfile = (data) => (dispatch) => {
  dispatch({
    type: UPDATE_PROFILE,
    payload: data,
  });
};

/**
 * After registration PATCH/POST, GET /me can briefly omit subject fields or return
 * them in a different order; merge the registration subject order into Redux so
 * Edit profile hydrates consistently.
 */
export const finalizeRegistrationProfile =
  (patchResponseUser, selectedSubjects = []) =>
  async (dispatch) => {
    const profileAfterGet = await dispatch(getProfile());
    const rawSelectedSubjectSnapshots = Array.isArray(selectedSubjects)
      ? selectedSubjects
          .map((subject) => buildSelectedSubjectSnapshot(subject))
          .filter(Boolean)
      : [];
    const patchRd = patchResponseUser?.roleDetails || {};

    if (!profileAfterGet) {
      return profileAfterGet;
    }

    const patchAvatarId =
      patchResponseUser?.avatarId ||
      patchResponseUser?.avatarID ||
      patchResponseUser?.avatar?.id ||
      patchResponseUser?.avatar?.mediaId ||
      null;
    const patchAvatarFallback =
      typeof patchResponseUser?.avatar === "string"
        ? patchResponseUser.avatar
        : null;
    const patchAvatarUrl =
      patchResponseUser?.avatarURL ||
      patchResponseUser?.avatarUrl ||
      patchResponseUser?.profilePicture ||
      patchAvatarFallback ||
      null;
    const getAvatarUrl =
      profileAfterGet.avatarURL || profileAfterGet.avatarUrl || null;
    const avatarIdsMatch =
      patchAvatarId && profileAfterGet.avatarId === patchAvatarId;
    const mergedAvatarUrl =
      avatarIdsMatch && getAvatarUrl ? getAvatarUrl : patchAvatarUrl;
    const shouldMergeAvatar =
      Boolean(patchAvatarId || patchAvatarUrl) &&
      (!avatarIdsMatch || !getAvatarUrl);

    const getRd = profileAfterGet.roleDetails || {};
    const patchSubjectIds = normalizeSubjectIdOrder(patchRd.selectedSubjectIds);
    const selectedSubjectSnapshots = mergeSelectedSubjectSnapshotsWithIds(
      rawSelectedSubjectSnapshots,
      patchSubjectIds,
    );
    const localSelectedSubjectIds = normalizeSubjectIdOrder(
      selectedSubjectSnapshots,
    );
    const shouldPreferPatchSubjects =
      patchSubjectIds.length > localSelectedSubjectIds.length ||
      (patchSubjectIds.length > 0 &&
        localSelectedSubjectIds.length > 0 &&
        !hasSameSubjectIdOrder(patchSubjectIds, localSelectedSubjectIds));
    const patchSubjects = localSelectedSubjectIds.length
      ? shouldPreferPatchSubjects
        ? patchSubjectIds
        : localSelectedSubjectIds
      : patchSubjectIds;
    const getSubjects = getRd.selectedSubjectIds;
    const patchExpertise = patchRd.expertise;
    const getExpertise = getRd.expertise;

    const subjectGapOrOrderMismatch =
      Array.isArray(patchSubjects) &&
      patchSubjects.length > 0 &&
      !hasSameSubjectIdOrder(getSubjects, patchSubjects);
    const expertiseGap =
      Array.isArray(patchExpertise) &&
      patchExpertise.length > 0 &&
      (!Array.isArray(getExpertise) || getExpertise.length === 0);
    const selectedSubjectSnapshotsGap =
      selectedSubjectSnapshots.length > 0 &&
      (!Array.isArray(profileAfterGet?.selectedSubjects) ||
        profileAfterGet.selectedSubjects.length === 0);

    if (
      !shouldMergeAvatar &&
      !subjectGapOrOrderMismatch &&
      !expertiseGap &&
      !selectedSubjectSnapshotsGap
    ) {
      return profileAfterGet;
    }

    const nextRd = { ...getRd };
    if (subjectGapOrOrderMismatch) nextRd.selectedSubjectIds = patchSubjects;
    if (expertiseGap) nextRd.expertise = patchExpertise;
    if (selectedSubjectSnapshots.length > 0) {
      nextRd.selectedSubjects = selectedSubjectSnapshots;
    }

    const nextProfile = {
      ...profileAfterGet,
      ...(patchAvatarId ? { avatarId: patchAvatarId } : {}),
      ...(mergedAvatarUrl
        ? {
            avatarUrl: mergedAvatarUrl,
            avatarURL: mergedAvatarUrl,
            profilePicture: mergedAvatarUrl,
          }
        : {}),
      roleDetails: nextRd,
      ...(selectedSubjectSnapshots.length > 0
        ? {
            selectedSubjects: selectedSubjectSnapshots,
            subjectNames: selectedSubjectSnapshots.map(
              (subject) => subject.name,
            ),
          }
        : {}),
    };
    dispatch(updateReduxProfile(normalizeProfile(nextProfile)));
    return nextProfile;
  };

export const userLogout =
  (options = {}) =>
  (dispatch) => {
    const logoutStartedAt = String(Date.now());
    const redirectUri =
      typeof options?.redirectUri === "string" && options.redirectUri.trim()
        ? options.redirectUri.trim()
        : `${window.location.origin}/`;
    ["dashboardSelectedSubject", "myListSelectedSubject", "logTime"].forEach(
      (key) => {
        localStorage.removeItem(key);
      },
    );
    localStorage.removeItem(KEYCLOAK_AUTH_FLAG);
    localStorage.removeItem("me-bootstrap-attempted");
    clearStoredKeycloakTokens();
    sessionStorage.clear();
    try {
      sessionStorage.setItem(LOGOUT_IN_PROGRESS_KEY, logoutStartedAt);
    } catch {}

    document.cookie.split(";").forEach((c) => {
      document.cookie = c
        .replace(/^ +/, "")
        .replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
    });

    dispatch({
      type: LOGOUT,
    });

    // Always call Keycloak logout
    if (keycloak.logout) {
      keycloak.logout({ redirectUri });
    } else {
      window.location.href = redirectUri;
    }
  };

// Infer user's role by querying role-details endpoints; returns a normalized role string or null
export const getUserRole = (userId) => async () => {
  try {
    if (!userId) return null;
    const authToken = await getToken();
    if (!authToken) return null;

    const rolesPriority = [
      "teacher",
      "lecturer",
      "mentor",
      "coordinator",
      "learner",
    ];

    const headers = { Authorization: authToken };
    // Fire in parallel, then pick the first success by priority order
    const requests = rolesPriority.map((role) =>
      axios
        .get(`/sns/v1/users/${userId}/roles/${role}`, { headers })
        .then((res) => {
          return { role, ok: res.status >= 200 && res.status < 300 };
        })
        .catch(() => {
          return { role, ok: false };
        }),
    );

    const results = await Promise.all(requests);
    for (const role of rolesPriority) {
      const match = results.find((r) => r.role === role && r.ok);
      if (match) return role;
    }
    return null;
  } catch {
    return null;
  }
};
